import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  FileText, Search, Filter, Shield, Clock, X, ArrowLeft, ArrowRight,
  Loader2, Tag, BookOpen,
} from "lucide-react";

const VERIF_TONE = {
  verified: "success",
  community: "neutral",
  draft: "warn",
};

export default function DocumentLibraryPage() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ categories: [], jurisdictions: [], languages: [], verification_statuses: [] });
  const [filter, setFilter] = useState({ q: "", category: "", jurisdiction: "", language: "", verification_status: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filter)) if (v) params.set(k, v);
    try {
      const r = await api.get(`/document-templates?${params.toString()}`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.category, filter.jurisdiction, filter.language, filter.verification_status]);
  useEffect(() => { api.get("/document-templates/facets").then((r) => setFacets(r.data)); }, []);

  const onSearch = (e) => { if (e.key === "Enter") load(); };

  return (
    <div data-testid="document-library-page">
      <PageHeader
        eyebrow="La Bibliothèque"
        title="Document Templates"
        description={
          facets.total
            ? `A curated library of ${facets.total} drafting-ready templates with jurisdiction, version and verification metadata. Pick a template, answer a few questions, publish a draft agreement.`
            : "Loading library…"
        }
      />

      {/* Search + filters */}
      <div className="flex items-center gap-2 mb-5">
        <div className="flex-1 flex items-center border border-[#E8E3F0] bg-white px-3">
          <Search size={16} className="text-[#4A5568]" />
          <input
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            onKeyDown={onSearch}
            placeholder="Search by title, description or tag…"
            className="flex-1 px-3 py-2.5 text-sm bg-transparent focus:outline-none"
            data-testid="library-search-input"
          />
          {filter.q && (
            <button onClick={() => { setFilter({ ...filter, q: "" }); setTimeout(load, 0); }} className="text-[#4A5568] hover:text-[#2A0F5A]"><X size={14} /></button>
          )}
        </div>
        <Button onClick={load} className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="library-search-btn">Search</Button>
      </div>

      <FilterRow
        label="Category"
        value={filter.category}
        options={facets.categories}
        onChange={(v) => setFilter({ ...filter, category: v })}
        testPrefix="lib-cat"
      />
      <FilterRow
        label="Jurisdiction"
        value={filter.jurisdiction}
        options={facets.jurisdictions}
        onChange={(v) => setFilter({ ...filter, jurisdiction: v })}
        testPrefix="lib-jur"
      />
      <FilterRow
        label="Verification"
        value={filter.verification_status}
        options={facets.verification_statuses}
        onChange={(v) => setFilter({ ...filter, verification_status: v })}
        testPrefix="lib-verif"
      />

      {loading ? (
        <div className="text-sm text-[#4A5568] flex items-center gap-2 mt-6">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={BookOpen} title="No templates match" description="Try removing a filter or clear the search." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {items.map((t) => (
            <Link
              key={t.id}
              to={`/app/templates/${t.id}`}
              className="group border border-[#E8E3F0] bg-white p-5 hover:border-[#2A0F5A] transition-colors flex flex-col"
              data-testid={`library-item-${t.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="label-eyebrow mb-1">{t.category}</div>
                  <h3 className="font-serif text-xl leading-tight">{t.title}</h3>
                </div>
                <StatusBadge tone={VERIF_TONE[t.verification_status] || "neutral"}>
                  <Shield size={10} className="inline mr-1" />{t.verification_status}
                </StatusBadge>
              </div>
              <p className="text-sm text-[#4A5568] flex-1 mb-4">{t.summary || t.description}</p>
              <div className="flex items-center justify-between text-xs text-[#4A5568] border-t border-[#E8E3F0] pt-3">
                <div className="flex items-center gap-3">
                  <span>{t.jurisdiction}</span>
                  <span>· {t.language}</span>
                  <span className="font-mono">· {t.version}</span>
                </div>
                <span className="inline-flex items-center gap-1"><Clock size={11} /> ~{t.estimated_time_min}m</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterRow({ label, value, options = [], onChange, testPrefix }) {
  if (!options.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <span className="label-eyebrow mr-1 flex items-center gap-1.5"><Filter size={11} /> {label}</span>
      <button
        onClick={() => onChange("")}
        className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${!value ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0] hover:border-[#7B56C9]"}`}
        data-testid={`${testPrefix}-all`}
      >All</button>
      {options.slice(0, 10).map((o) => (
        <button
          key={o.name}
          onClick={() => onChange(value === o.name ? "" : o.name)}
          className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border ${value === o.name ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0] hover:border-[#7B56C9]"}`}
          data-testid={`${testPrefix}-${o.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {o.name} <span className="opacity-70 ml-1 normal-case tracking-normal">({o.count})</span>
        </button>
      ))}
    </div>
  );
}

// ---------- Template detail + Create from Template → Agreement wizard -------
export function DocumentTemplateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [answers, setAnswers] = useState({});
  const [title, setTitle] = useState("");
  const [counterparty, setCounterparty] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/document-templates/${id}`).then((r) => {
      setT(r.data);
      setTitle(r.data.title);
    }).catch(() => toast.error("Template not found"));
  }, [id]);

  const create = async () => {
    if (!title.trim()) return toast.error("Give it a title");
    setSubmitting(true);
    try {
      const r = await api.post(`/document-templates/${id}/instantiate`, {
        title, answers,
        counterparty: counterparty || undefined,
      });
      toast.success("Agreement draft created");
      navigate(`/app/agreements/${r.data.agreement_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    } finally { setSubmitting(false); }
  };

  if (!t) return <div className="text-sm text-[#4A5568] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div data-testid="template-detail-page">
      <Link to="/app/templates" className="label-eyebrow inline-flex items-center gap-1.5 mb-4 hover:text-[#2A0F5A]" data-testid="back-to-library">
        <ArrowLeft size={12} /> Library
      </Link>
      <PageHeader
        eyebrow={`${t.category} · ${t.jurisdiction} · ${t.language}`}
        title={t.title}
        description={t.summary || t.description}
        action={
          <StatusBadge tone={VERIF_TONE[t.verification_status] || "neutral"}>
            <Shield size={10} className="inline mr-1" />{t.verification_status} · {t.version}
          </StatusBadge>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="label-eyebrow">Fill in the details</div>
          <div>
            <Label className="label-eyebrow mb-1.5 block">Agreement title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="rounded-none border-[#E8E3F0]"
              data-testid="agreement-title-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 block">Counterparty (optional)</Label>
            <Input
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              className="rounded-none border-[#E8E3F0]"
              placeholder="e.g. Acme Corp"
              data-testid="agreement-counterparty-input"
            />
          </div>

          {(t.questions || []).map((q) => (
            <div key={q.key}>
              <Label className="label-eyebrow mb-1.5 block">{q.label}</Label>
              {q.type === "textarea" ? (
                <Textarea
                  value={answers[q.key] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                  rows={3}
                  className="rounded-none border-[#E8E3F0]"
                  data-testid={`tpl-q-${q.key}`}
                />
              ) : (
                <Input
                  value={answers[q.key] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                  className="rounded-none border-[#E8E3F0]"
                  data-testid={`tpl-q-${q.key}`}
                />
              )}
            </div>
          ))}

          <Button
            onClick={create}
            disabled={submitting}
            className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
            data-testid="create-agreement-btn"
          >
            {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Creating…</> : <>Create agreement <ArrowRight size={14} className="ml-2" /></>}
          </Button>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-6 border border-[#E8E3F0] bg-[#FAFAF8] p-5 space-y-4 text-sm">
            <div>
              <div className="label-eyebrow mb-2">Template preview</div>
              <div className="text-[12px] font-mono whitespace-pre-wrap text-[#2A0F5A]/80 max-h-[55vh] overflow-y-auto border border-[#E8E3F0] bg-white p-3">
                {(t.body || "").slice(0, 2000)}
                {(t.body || "").length > 2000 && "\n…"}
              </div>
            </div>
            {(t.tags || []).length > 0 && (
              <div>
                <div className="label-eyebrow mb-2 flex items-center gap-1"><Tag size={11} /> Tags</div>
                <div className="flex flex-wrap gap-1.5">
                  {(t.tags || []).map((tag) => (
                    <span key={tag} className="text-[10px] tracking-[0.14em] uppercase border border-[#E8E3F0] bg-white px-2 py-0.5">{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
